import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@gto/ui/button'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import { AppDialog } from '@/components/app-dialog'
import {
  type AffiliateCreateCouponSchema,
  buildCouponBatchRequest,
  buildCouponCreateRequest,
  couponBatchFormSchema,
  type CouponBatchFormValues,
  type CouponBatchRequest,
  type CouponCreateSharedFormValues,
  type CouponPlan,
  couponSingleFormSchema,
  type CouponSingleFormValues,
  DEFAULT_COUPON_DISCOUNT_PERCENT,
  resolveSubmitPlanIds,
} from '@/domain/coupons'

import { ControlledNumberField } from './controlled-number-field'
import { ControlledTextField } from './controlled-text-field'
import { CouponFormError } from './coupon-form-error'
import { CouponSharedFields, type CouponTagsState } from './coupon-shared-fields'
import { CouponSubmitButton } from './coupon-submit-button'
import { CouponSwitchRow } from './coupon-switch-row'
import { useCouponCodeServerError } from './use-coupon-code-server-error'

type Mode = 'single' | 'batch'

const CREATE_SHARED_DEFAULTS: CouponCreateSharedFormValues = {
  tagIds: [],
  discountValue: '',
  daysValid: '365',
  isOneUsePerUser: true,
  firstPaymentOnly: false,
  autoRenewal: false,
  scopeKind: 'all',
  scopePlanId: '',
  scopeTier: '',
  scopeProduct: '',
  scopeInterval: '',
  customPlanIds: [],
}

// A single link pre-fills DEFAULT_COUPON_DISCOUNT_PERCENT (the common case) but
// starts blank on both usage limits: empty `maxUses` = unlimited total
// redemptions, empty `daysValid` = no expiry — the operator opts into a cap or
// window rather than inheriting one. Batch keeps CREATE_SHARED_DEFAULTS (blank
// discount, 365-day validity) and has no max-uses field (fixed at 1 server
// side).
const singleDefaults: CouponSingleFormValues = {
  ...CREATE_SHARED_DEFAULTS,
  code: '',
  discountValue: String(DEFAULT_COUPON_DISCOUNT_PERCENT),
  maxUses: '',
  daysValid: '',
}
const batchDefaults: CouponBatchFormValues = {
  ...CREATE_SHARED_DEFAULTS,
  count: '10',
  codePrefix: '',
}

const AutoRenewalField = () => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<CouponCreateSharedFormValues>()
  const label = formatMessage({ id: 'coupon.autoRenewal' })

  return (
    <Controller
      control={control}
      name="autoRenewal"
      render={({ field }) => (
        <CouponSwitchRow
          checked={field.value}
          description={formatMessage({ id: 'coupon.autoRenewalCreateHelper' })}
          label={label}
          onCheckedChange={field.onChange}
        />
      )}
    />
  )
}

type PlansState = {
  plans: CouponPlan[]
  plansLoading: boolean
  plansError: boolean
}

type CouponCreateDialogProps = PlansState & {
  tagState: CouponTagsState
  open: boolean
  submitting: boolean
  /** Latest single-create mutation error — a 422 on `code` maps to the field. */
  singleError: unknown
  /** Latest batch-create mutation error — surfaces as the in-dialog banner. */
  batchError: unknown
  onClose: () => void
  onSubmitSingle: (body: AffiliateCreateCouponSchema) => void
  onSubmitBatch: (body: CouponBatchRequest) => void
}

export const CouponCreateDialog = ({
  open,
  submitting,
  singleError,
  batchError,
  plans,
  plansLoading,
  plansError,
  tagState,
  onClose,
  onSubmitSingle,
  onSubmitBatch,
}: CouponCreateDialogProps) => {
  const { formatMessage } = useIntl()
  const [mode, setMode] = useState<Mode>('single')

  return (
    <AppDialog
      open={open}
      width={560}
      onClose={onClose}
    >
      <AppDialog.Header title={formatMessage({ id: 'admin.partnerDetail.coupons.create' })} />
      <ToggleButtonGroup
        aria-label={formatMessage({ id: 'coupon.mode.label' })}
        size="small"
        value={mode}
        onValueChange={(value: string) => {
          // Widened to `string`: the toggle can emit an empty value on deselect,
          // which the narrow `Mode` param type would let slip past this guard.
          if (value === 'single' || value === 'batch') {
            setMode(value)
          }
        }}
      >
        <ToggleButton value="single">{formatMessage({ id: 'coupon.mode.single' })}</ToggleButton>
        <ToggleButton value="batch">{formatMessage({ id: 'coupon.mode.batch' })}</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'single' ? (
        <SingleCouponForm
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
          serverError={singleError}
          submitting={submitting}
          tagState={tagState}
          onCancel={onClose}
          onSubmit={onSubmitSingle}
        />
      ) : (
        <BatchCouponForm
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
          serverError={batchError}
          submitting={submitting}
          tagState={tagState}
          onCancel={onClose}
          onSubmit={onSubmitBatch}
        />
      )}
    </AppDialog>
  )
}

type SingleFormProps = PlansState & {
  tagState: CouponTagsState
  submitting: boolean
  serverError: unknown
  onCancel: () => void
  onSubmit: (body: AffiliateCreateCouponSchema) => void
}

const SingleCouponForm = ({
  plans,
  plansLoading,
  plansError,
  tagState,
  submitting,
  serverError,
  onCancel,
  onSubmit,
}: SingleFormProps) => {
  const { formatMessage } = useIntl()
  const form = useForm<CouponSingleFormValues>({
    resolver: zodResolver(couponSingleFormSchema),
    defaultValues: singleDefaults,
  })
  useCouponCodeServerError(serverError, (message) =>
    form.setError('code', { type: 'server', message }),
  )

  // Base UI parks focus on the popup container when the dialog opens; defer one
  // frame so focus lands on the first field and the operator can type at once.
  useEffect(
    function focusCodeOnOpen() {
      const raf = requestAnimationFrame(() => form.setFocus('code'))

      return () => cancelAnimationFrame(raf)
    },
    [form],
  )

  const submit = form.handleSubmit((values) => {
    // Belt-and-suspenders: the submit button is already disabled for a null or
    // no-match scope, but never let one through (a no-match scope would send
    // plan_ids: [] = all plans).
    const planIds = resolveSubmitPlanIds(values, plans)
    if (planIds === null) {
      return
    }
    onSubmit(buildCouponCreateRequest(values, planIds))
  })

  return (
    <FormProvider {...form}>
      <AppDialog.Body>
        <CouponFormError serverError={serverError} />
        <ControlledTextField
          control={form.control}
          data-tst="coupon-code"
          label={formatMessage({ id: 'admin.partnerDetail.coupons.code' })}
          maxLength={64}
          name="code"
        />
        <CouponSharedFields
          namesDefaultDiscount
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
          tagState={tagState}
          maxUsesSlot={
            <ControlledNumberField
              control={form.control}
              helperText={formatMessage({ id: 'coupon.maxUsesHelper' })}
              label={formatMessage({ id: 'coupon.maxUses' })}
              min={1}
              name="maxUses"
            />
          }
        />
        <AutoRenewalField />
      </AppDialog.Body>
      <AppDialog.Footer>
        <Button onClick={onCancel}>
          <FormattedMessage id="admin.common.cancel" />
        </Button>
        <CouponSubmitButton
          data-tst="coupon-submit-single"
          label={<FormattedMessage id="admin.common.create" />}
          plans={plans}
          submitting={submitting || tagState.creatingTag}
          onClick={() => void submit()}
        />
      </AppDialog.Footer>
    </FormProvider>
  )
}

type BatchFormProps = PlansState & {
  tagState: CouponTagsState
  submitting: boolean
  serverError: unknown
  onCancel: () => void
  onSubmit: (body: CouponBatchRequest) => void
}

const BatchCouponForm = ({
  plans,
  plansLoading,
  plansError,
  tagState,
  submitting,
  serverError,
  onCancel,
  onSubmit,
}: BatchFormProps) => {
  const { formatMessage } = useIntl()
  const form = useForm<CouponBatchFormValues>({
    resolver: zodResolver(couponBatchFormSchema),
    defaultValues: batchDefaults,
  })

  // Match single mode: focus the first batch field once the dialog settles.
  useEffect(
    function focusCountOnOpen() {
      const raf = requestAnimationFrame(() => form.setFocus('count'))

      return () => cancelAnimationFrame(raf)
    },
    [form],
  )

  const submit = form.handleSubmit((values) => {
    const planIds = resolveSubmitPlanIds(values, plans)
    if (planIds === null) {
      return
    }
    onSubmit(buildCouponBatchRequest(values, planIds))
  })

  return (
    <FormProvider {...form}>
      <AppDialog.Body>
        <CouponFormError serverError={serverError} />
        <ControlledNumberField
          control={form.control}
          data-tst="coupon-batch-count"
          helperText={formatMessage({ id: 'coupon.batch.countHelper' })}
          label={formatMessage({ id: 'coupon.batch.count' })}
          max={500}
          min={1}
          name="count"
        />
        <ControlledTextField
          control={form.control}
          data-tst="coupon-batch-prefix"
          helperText={formatMessage({ id: 'coupon.batch.prefixHelper' })}
          label={formatMessage({ id: 'coupon.batch.prefix' })}
          maxLength={32}
          name="codePrefix"
        />
        <CouponSharedFields
          plans={plans}
          plansError={plansError}
          plansLoading={plansLoading}
          showOneUsePerUser={false}
          tagState={tagState}
        />
        <AutoRenewalField />
        <Typography
          color="text.secondary"
          variant="caption"
        >
          {formatMessage({ id: 'coupon.batch.maxUsesNote' })}
        </Typography>
      </AppDialog.Body>
      <AppDialog.Footer>
        <Button onClick={onCancel}>
          <FormattedMessage id="admin.common.cancel" />
        </Button>
        <CouponSubmitButton
          data-tst="coupon-submit-batch"
          label={<FormattedMessage id="coupon.batch.generate" />}
          plans={plans}
          submitting={submitting || tagState.creatingTag}
          onClick={() => void submit()}
        />
      </AppDialog.Footer>
    </FormProvider>
  )
}
